import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { checkRoom, classGame, whileUsed } from '../game/client';
import { useClassCode } from '../context/ClassCodeContext';
import { loadSeat, saveSeat } from '../game/seat';
import { loadPlayer } from '../game/player';
import { useClass } from './classContext';
import { reducedMotion } from './chalk';
import { useStrings } from './strings';
import { SlimeMark } from './icons';

export interface OpenGame {
  code: string;
  section: string;
}

// The game a teacher opened for one of this device's classes (or just `section`), checked now and every 10 seconds
// while someone is using the page. A laptop signed into two classes can find both rooms open (one left open from an
// earlier lesson): it goes to the one opened last, the lesson on now, so the class isn't split between rooms.
export function useOpenGame(section?: string): OpenGame | null {
  const { passes: all } = useClassCode();
  const passes = section ? all.filter((p) => p.section === section) : all;
  const [game, setGame] = useState<OpenGame | null>(null);
  const key = passes.map((p) => p.pass).join(' ');
  useEffect(() => {
    let live = true;
    const check = async () => {
      const best = await newestGame(passes);
      if (!live) return;
      if (!best) return setGame(null);
      const { code, section: sec } = best;
      setGame((g) => (g?.code === code && g.section === sec ? g : { code, section: sec }));
    };
    const stop = whileUsed(check, 10_000); // sleeps while nobody is using the page
    return () => {
      live = false;
      stop();
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return game;
}

export const useClassGame = (section?: string): string | null => useOpenGame(section)?.code ?? null;

// Of the games open for these classes, the one opened last
async function newestGame(passes: { pass: string; section: string }[]): Promise<OpenGame | null> {
  const found = await Promise.all(passes.map((p) => classGame(p.pass).then((g) => g && { ...g, section: p.section })));
  let best: { code: string; at: number; section: string } | null = null;
  for (const g of found) if (g && (!best || g.at > best.at)) best = g;
  return best && { code: best.code, section: best.section };
}

// The room I was in has closed (the teacher closed it, or restarted on another laptop): while `waiting`, look every
// few seconds for that class's (`section`) next room, whether or not anyone is touching the laptop (a student who
// wasn't watching still goes along), for up to 20 minutes, and answer its code once there is one that isn't `left`.
// Only that class: another class's room left open from an earlier lesson isn't where the class went.
export function useNextClassGame(waiting: boolean, left: string, section: string | null): string | null {
  const { passes: all } = useClassCode();
  const passes = all.filter((p) => p.section === section);
  const [next, setNext] = useState<string | null>(null);
  const key = passes.map((p) => p.pass).join(' ');
  useEffect(() => {
    if (!waiting || !passes.length) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const until = Date.now() + FOLLOW_FOR_MS; // a laptop left open on a closed room stops asking after a while
    const look = async () => {
      if (Date.now() > until) return;
      const g = await newestGame(passes);
      if (!live) return;
      if (g && g.code !== left && (await checkRoom(g.code)) === 'open') return live && setNext(g.code);
      timer = setTimeout(look, FOLLOW_MS);
    };
    void look();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [waiting, left, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return waiting ? next : null;
}
const FOLLOW_MS = 5000;
const FOLLOW_FOR_MS = 20 * 60_000;

// The site checks once for every page, so the note, the board and Play all agree.
const LiveCtx = createContext<OpenGame | null>(null);
export function LiveProvider({ children }: { children: ReactNode }) {
  return <LiveCtx.Provider value={useOpenGame()}>{children}</LiveCtx.Provider>;
}
export const useLive = () => useContext(LiveCtx);

// Joining a game by its code: the slime walks in through the chalk door, unless motion is turned down.
export function useJoin() {
  const { s } = useStrings();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState('');
  // (before: what happens on the page first, once the game is there to go to: the Slime page carries its slime over)
  const join = async (code: string, before?: () => Promise<unknown>) => {
    const player = loadPlayer();
    if (!player) return void navigate('/play'); // no slime on this device: bring one back or make one first
    if (loadSeat()?.code === code) {
      await before?.();
      return void navigate('/play/live', { state: { door: !reducedMotion() } });
    }
    setChecking(true);
    setProblem('');
    const room = await checkRoom(code);
    setChecking(false);
    if (room !== 'open') {
      setProblem(room === 'missing' ? s.notFound : s.unreachable);
      return false;
    }
    saveSeat({ code, name: player.name, player: player.code });
    await before?.();
    navigate('/play/live', { state: { door: !reducedMotion() } });
    return true;
  };
  return { join, checking, problem };
}

// "Your class dagashiya is open": chalked across the top of every page while a class game is open. No sound, no
// popup, nothing to close; it goes when the game does.
export function LiveNote() {
  const live = useLive();
  const { sections } = useClass();
  const { s } = useStrings();
  const { join, checking, problem } = useJoin();
  // the Slime page shows the class game itself: its own Join the class, or, with no slime yet, the way to make one
  const onSlimePage = useLocation().pathname === '/play';
  if (!live || onSlimePage) return null;
  const name = sections.find((p) => p.section === live.section)?.name;
  const player = loadPlayer();
  return (
    <div className="live-note" role="status">
      <SlimeMark className="live-slime" />
      <p className="live-say">
        <span className="hand">{s.dagashiyaOpen}</span>
        <span className="soft">
          {name && (
            <>
              <span translate="no">{name}</span>
              {' · '}
            </>
          )}
          {s.teacherOpened}
        </span>
      </p>
      {player ? (
        <button className="btn go live-join" type="button" disabled={checking} onClick={() => join(live.code)}>
          {checking ? s.checking : loadSeat()?.code === live.code ? s.rejoinShort : s.join}
        </button>
      ) : (
        <Link className="btn go live-join" to="/play">
          {s.join}
        </Link>
      )}
      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
